<template>
<div class="review-invitation-container">
    <!-- Header -->
    <div class="header-container">
        <a href="#" class="brand w-nav-brand"><img sizes="84.03125px" alt="" :src="logo" loading="lazy" class="image-389" style="height: 45px;"></a>
        <h1 class="page-title">Send Review Invitation</h1>
    </div>

    <!-- User Search Section -->
    <div class="search-card md-card">
        <h2 class="md-card-title">Find User</h2>

        <div class="md-form-group">
            <label>User ID <span style="color: red;">*</span></label>
            <div class="search-input-group">
                <input
                    type="text"
                    v-model="searchUserId"
                    class="md-input"
                    placeholder="Enter User ID"
                    @keyup.enter="fetchUserDetails"
                >
                <button
                    @click="fetchUserDetails"
                    class="md-btn md-btn-primary"
                    :disabled="!searchUserId || searching"
                >
                    <i class="material-icons">search</i> Search
                </button>
            </div>
        </div>
    </div>

    <!-- Loading State -->
    <div v-if="searching" class="loading">
        <div class="md-loader"></div>
        <p>Searching for user...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error md-card md-card-error">
        <i class="material-icons">error</i>
        <p>{{ error }}</p>
    </div>

    <!-- User Details Section -->
    <div v-else-if="user" class="main-content">
        <!-- User Info Card -->
        <div class="user-card md-card">
            <h2 class="md-card-title">User Details</h2>

            <div class="user-info">
                <div class="info-row">
                    <span class="label">Name:</span>
                    <span class="value">{{ user.first_name }} {{ user.last_name }}</span>
                </div>

                <div class="info-row">
                    <span class="label">Email:</span>
                    <span class="value">{{ user.email }}</span>
                </div>

                <div v-if="user.phone" class="info-row">
                    <span class="label">Phone:</span>
                    <span class="value">{{ user.phone }}</span>
                </div>

                <div v-if="user.id" class="info-row">
                    <span class="label">User ID:</span>
                    <span class="value">{{ user.id }}</span>
                </div>
            </div>
        </div>

        <!-- Rating Selection Card -->
        <div class="rating-card md-card">
            <h2 class="md-card-title">Send Review Invitation</h2>

            <div class="md-form-group">
                <label>Select Rating (Optional)</label>
                <select v-model="selectedRating" class="md-select">
                    <option value="">No specific rating</option>
                    <option value="1">1 Star</option>
                    <option value="2">2 Stars</option>
                    <option value="3">3 Stars</option>
                    <option value="4">4 Stars</option>
                    <option value="5">5 Stars</option>
                </select>
                <small class="helper-text">Optional: Pre-select a rating for the user</small>
            </div>

            <div class="action-buttons">
                <button
                    @click="sendReviewInvitation"
                    class="md-btn md-btn-primary md-btn-large"
                    :disabled="sending || !user.phone"
                >
                    <i class="material-icons">send</i>
                    {{ sending ? 'Sending...' : 'Send Review Invitation' }}
                </button>
            </div>

            <div v-if="!user.phone" class="md-alert md-alert-warning">
                <i class="material-icons">warning</i>
                <p>User does not have a phone number. SMS cannot be sent.</p>
            </div>
        </div>

        <!-- Success Message -->
        <div v-if="successMessage" class="success-card md-card md-card-success">
            <i class="material-icons">check_circle</i>
            <p>{{ successMessage }}</p>
        </div>

        <!-- Reviews Section -->
        <div class="reviews-card md-card">
            <div class="reviews-header">
                <h2 class="md-card-title">
                    <i class="material-icons">star</i> User Reviews
                </h2>
                <button
                    @click="fetchUserReviews"
                    class="md-btn md-btn-secondary"
                    :disabled="loadingReviews"
                >
                    <i class="material-icons">refresh</i>
                    {{ loadingReviews ? 'Loading...' : 'Refresh' }}
                </button>
            </div>

            <!-- Loading Reviews -->
            <div v-if="loadingReviews" class="reviews-loading">
                <div class="md-loader-small"></div>
                <p>Loading reviews...</p>
            </div>

            <!-- Reviews Error -->
            <div v-else-if="reviewsError" class="md-alert md-alert-error">
                <i class="material-icons">error</i>
                <p>{{ reviewsError }}</p>
            </div>

            <!-- No Reviews -->
            <div v-else-if="reviews.length === 0" class="no-reviews">
                <i class="material-icons">rate_review</i>
                <p>No reviews found for this user yet.</p>
            </div>

            <!-- Reviews List -->
            <div v-else class="reviews-list">
                <div v-for="review in reviews" :key="review.id" class="review-item md-panel">
                    <div class="review-header">
                        <div class="review-rating">
                            <span v-for="star in 5" :key="star" class="star">
                                <i class="material-icons" :class="star <= review.rating ? 'star-filled' : 'star-empty'">
                                    {{ star <= review.rating ? 'star' : 'star_border' }}
                                </i>
                            </span>
                            <span class="rating-text">{{ review.rating }}/5</span>
                        </div>
                        <span class="review-date">{{ formatDate(review.created_at) }}</span>
                    </div>

                    <div v-if="review.review_text" class="review-content">
                        <p>{{ review.review_text }}</p>
                    </div>

                    <div class="review-meta">
                        <span v-if="review.platform" class="meta-item">
                            <i class="material-icons">platform</i>
                            {{ review.platform }}
                        </span>
                        <span v-if="review.status" class="meta-item">
                            <span :class="['status-badge', getReviewStatusClass(review.status)]">
                                {{ review.status }}
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state md-card">
        <i class="material-icons">person_search</i>
        <p>Enter a User ID to search for a user and send a review invitation</p>
    </div>
</div>
</template>

<script>
export default {
    name: 'UserReviewInvitations',
    data() {
        return {
            logo: process.env.VUE_APP_LOGO,
            baseUrl: process.env.VUE_APP_API_URL,
            searchUserId: '',
            userId: null,
            user: null,
            selectedRating: '',
            searching: false,
            sending: false,
            error: null,
            successMessage: null,
            reviews: [],
            loadingReviews: false,
            reviewsError: null
        }
    },
    mounted() {
        // Add Material Icons
        if (!document.querySelector('link[href*="material-icons"]')) {
            const link = document.createElement('link');
            link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        }

        // Check if user ID is passed via route params
        if (this.$route.params.id) {
            this.searchUserId = this.$route.params.id;
            this.fetchUserDetails();
        }
    },
    methods: {
        // No notification plugin is registered ($notify doesn't exist) — a
        // direct this.$notify() call throws. Route through this safe helper.
        notify(payload) {
            /* eslint-disable no-restricted-syntax -- sole sanctioned $notify access */
            if (typeof this.$notify === 'function') {
                this.$notify(payload);
            } else {
                console.log(`[${payload.type}] ${payload.text}`);
            }
            /* eslint-enable no-restricted-syntax */
        },

        async fetchUserDetails() {
            if (!this.searchUserId) {
                this.error = 'Please enter a User ID';
                return;
            }

            this.searching = true;
            this.error = null;
            this.successMessage = null;
            this.user = null;

            try {
                const response = await this.$http.get(`/users/${this.searchUserId}`);

                if (response.data && response.data.user) {
                    this.user = response.data.user;
                    this.userId = response.data.user.id;

                    // Fetch reviews after user is loaded
                    await this.fetchUserReviews();
                } else {
                    this.error = 'User not found';
                }
            } catch (err) {
                console.error('Error fetching user:', err);
                this.error = err.response?.data?.message || 'Failed to fetch user details. Please check the User ID and try again.';
            } finally {
                this.searching = false;
            }
        },

        async fetchUserReviews() {
            if (!this.userId) {
                return;
            }

            this.loadingReviews = true;
            this.reviewsError = null;

            try {
                const response = await this.$http.get('/user-reviews', {
                    params: {
                        userId: this.userId
                    }
                });

                if (response.data) {
                    // Handle different possible response structures
                    if (Array.isArray(response.data)) {
                        this.reviews = response.data;
                    } else if (response.data.reviews && Array.isArray(response.data.reviews)) {
                        this.reviews = response.data.reviews;
                    } else if (response.data.data && Array.isArray(response.data.data)) {
                        this.reviews = response.data.data;
                    } else {
                        this.reviews = [];
                    }
                } else {
                    this.reviews = [];
                }
            } catch (err) {
                console.error('Error fetching reviews:', err);
                this.reviewsError = err.response?.data?.message || 'Failed to load reviews. Please try again.';
                this.reviews = [];
            } finally {
                this.loadingReviews = false;
            }
        },

        formatDate(dateString) {
            if (!dateString) return 'N/A';

            const date = new Date(dateString);
            const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
            return date.toLocaleDateString('en-US', options);
        },

        getReviewStatusClass(status) {
            const statusMap = {
                'published': 'status-success',
                'pending': 'status-warning',
                'approved': 'status-success',
                'rejected': 'status-error',
                'active': 'status-success',
                'inactive': 'status-neutral'
            };
            return statusMap[status?.toLowerCase()] || 'status-neutral';
        },

        async sendReviewInvitation() {
            if (!this.user) {
                this.error = 'No user selected';
                return;
            }

            if (!this.user.phone) {
                this.error = 'User does not have a phone number';
                return;
            }

            this.sending = true;
            this.error = null;
            this.successMessage = null;

            try {
                const params = {
                    userId: this.userId,
                    sendSms: true
                };

                // Add rate parameter if a rating is selected
                if (this.selectedRating) {
                    params.rate = this.selectedRating;
                }

                const response = await this.$http.get('/api/rating-redirect', {
                    params: params
                });

                this.successMessage = response.data?.message || `Review invitation sent successfully to ${this.user.first_name} ${this.user.last_name} at ${this.user.phone}`;

                this.notify({
                    title: 'Success',
                    message: 'Review invitation sent successfully!',
                    type: 'success'
                });

                // Reset rating selection after successful send
                this.selectedRating = '';

                // Refresh reviews after sending invitation
                await this.fetchUserReviews();
            } catch (err) {
                console.error('Error sending review invitation:', err);
                this.error = err.response?.data?.message || 'Failed to send review invitation. Please try again.';

                this.notify({
                    title: 'Error',
                    message: this.error,
                    type: 'error'
                });
            } finally {
                this.sending = false;
            }
        }
    }
}
</script>

<style scoped>
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

.review-invitation-container {
    min-height: 100vh;
    background-color: #f5f5f5;
    padding: 20px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* Header */
.header-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 30px;
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.page-title {
    font-size: 24px;
    color: #333;
    font-weight: 600;
}

/* Material Design Cards */
.md-card {
    background-color: white;
    border-radius: 8px;
    padding: 24px;
    margin-bottom: 20px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.3s ease;
}

.md-card:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.md-card-title {
    font-size: 20px;
    color: #333;
    margin-bottom: 20px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 10px;
}

.md-card-title i {
    color: #2196F3;
}

/* Search Section */
.search-card {
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
}

.search-input-group {
    display: flex;
    gap: 10px;
}

.search-input-group .md-input {
    flex: 1;
}

/* Form Elements */
.md-form-group {
    margin-bottom: 20px;
}

.md-form-group label {
    display: block;
    margin-bottom: 8px;
    color: #333;
    font-weight: 500;
    font-size: 14px;
}

.md-input,
.md-select {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.md-input:focus,
.md-select:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
}

.helper-text {
    display: block;
    margin-top: 5px;
    font-size: 12px;
    color: #757575;
}

/* Buttons */
.md-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.md-btn i {
    font-size: 18px;
}

.md-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.md-btn-primary {
    background-color: #2196F3;
    color: white;
}

.md-btn-primary:hover:not(:disabled) {
    background-color: #1976D2;
    box-shadow: 0 4px 8px rgba(33, 150, 243, 0.3);
}

.md-btn-large {
    padding: 14px 28px;
    font-size: 16px;
}

/* Loading State */
.loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    background-color: white;
    border-radius: 8px;
    margin-bottom: 20px;
}

.md-loader {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #2196F3;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.loading p {
    margin-top: 16px;
    color: #757575;
    font-size: 14px;
}

/* Error Card */
.md-card-error {
    background-color: #ffebee;
    border-left: 4px solid #f44336;
}

.error {
    display: flex;
    align-items: center;
    gap: 12px;
    color: #c62828;
}

.error i {
    font-size: 24px;
}

/* Success Card */
.md-card-success {
    background-color: #e8f5e9;
    border-left: 4px solid #4CAF50;
}

.success-card {
    display: flex;
    align-items: center;
    gap: 12px;
    color: #2e7d32;
}

.success-card i {
    font-size: 24px;
}

/* Alert */
.md-alert {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 4px;
    margin-top: 16px;
}

.md-alert i {
    font-size: 20px;
}

.md-alert-warning {
    background-color: #fff3e0;
    color: #e65100;
    border-left: 4px solid #ff9800;
}

/* User Info */
.user-info {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.info-row {
    display: flex;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;
}

.info-row:last-child {
    border-bottom: none;
}

.info-row .label {
    font-weight: 600;
    color: #555;
    font-size: 14px;
}

.info-row .value {
    color: #333;
    font-size: 14px;
}

/* Action Buttons */
.action-buttons {
    display: flex;
    justify-content: center;
    margin-top: 20px;
}

/* Empty State */
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
}

.empty-state i {
    font-size: 64px;
    color: #bdbdbd;
    margin-bottom: 16px;
}

.empty-state p {
    color: #757575;
    font-size: 16px;
    max-width: 400px;
}

/* Main Content */
.main-content {
    max-width: 800px;
    margin: 0 auto;
}

/* Reviews Section */
.reviews-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.reviews-header .md-card-title {
    margin-bottom: 0;
}

.md-btn-secondary {
    background-color: #757575;
    color: white;
}

.md-btn-secondary:hover:not(:disabled) {
    background-color: #616161;
    box-shadow: 0 4px 8px rgba(117, 117, 117, 0.3);
}

.reviews-loading {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px;
    justify-content: center;
}

.md-loader-small {
    width: 24px;
    height: 24px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #2196F3;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

.reviews-loading p {
    color: #757575;
    font-size: 14px;
    margin: 0;
}

.no-reviews {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;
}

.no-reviews i {
    font-size: 48px;
    color: #bdbdbd;
    margin-bottom: 12px;
}

.no-reviews p {
    color: #757575;
    font-size: 14px;
}

.md-alert-error {
    background-color: #ffebee;
    color: #c62828;
    border-left: 4px solid #f44336;
}

/* Reviews List */
.reviews-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.review-item {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px;
    background-color: #fafafa;
    transition: all 0.3s ease;
}

.review-item:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background-color: white;
}

.md-panel {
    background-color: #fafafa;
}

.review-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    flex-wrap: wrap;
    gap: 10px;
}

.review-rating {
    display: flex;
    align-items: center;
    gap: 8px;
}

.star {
    display: inline-flex;
}

.star i {
    font-size: 18px;
}

.star-filled {
    color: #FFC107;
}

.star-empty {
    color: #e0e0e0;
}

.rating-text {
    font-weight: 600;
    color: #333;
    font-size: 14px;
}

.review-date {
    color: #757575;
    font-size: 13px;
}

.review-content {
    margin: 12px 0;
    padding: 12px;
    background-color: white;
    border-radius: 4px;
    border-left: 3px solid #2196F3;
}

.review-content p {
    color: #333;
    font-size: 14px;
    line-height: 1.6;
    margin: 0;
}

.review-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
    flex-wrap: wrap;
}

.meta-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    color: #757575;
}

.meta-item i {
    font-size: 16px;
}

/* Status Badges */
.status-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    text-transform: capitalize;
}

.status-success {
    background-color: #e8f5e9;
    color: #2e7d32;
}

.status-warning {
    background-color: #fff3e0;
    color: #e65100;
}

.status-error {
    background-color: #ffebee;
    color: #c62828;
}

.status-neutral {
    background-color: #f5f5f5;
    color: #757575;
}

/* Responsive Design */
@media (max-width: 768px) {
    .header-container {
        flex-direction: column;
        gap: 15px;
        text-align: center;
    }

    .page-title {
        font-size: 20px;
    }

    .search-input-group {
        flex-direction: column;
    }

    .info-row {
        flex-direction: column;
        gap: 8px;
    }

    .md-card {
        padding: 16px;
    }

    .reviews-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }

    .review-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .review-rating {
        margin-bottom: 8px;
    }
}

@media (max-width: 480px) {
    .review-invitation-container {
        padding: 10px;
    }

    .md-btn-large {
        width: 100%;
    }

    .star i {
        font-size: 16px;
    }

    .review-item {
        padding: 12px;
    }
}
</style>
